import { FunctionComponent, memo, useContext } from "react";

import { IProps } from "./delete-tag-group.types";
import "./delete-tag-group.scss";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { faTrash, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import Modal, { ModalContent, ModalFooter, ModalHeader, useModal } from "@edgetier/modal";
import { Button } from "@edgetier/components";
import { axios } from "utilities/axios";
import { ITag, ITagGroup, Route } from "@edgetier/watchtower-types";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";

/**
 * A button that, when clicked, opens a modal for deleting a tag group.
 * @param props.tagGroup   The name of the tag group.
 * @param props.tagGroupId The ID of the tag group.
 */
const DeleteTagGroup: FunctionComponent<IProps> = ({ tagGroup, tagGroupId }) => {
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);

    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });
    const queryClient = useQueryClient();

    /**
     * Delete the tag group.
     */
    const deleteTagGroup = async (): Promise<void> => {
        try {
            await axios.delete(Route.TagGroup.replace(":tagGroupId", tagGroupId));
            queryClient.setQueriesData<ITag[]>(Route.Tags, (tags) => {
                return (tags ?? []).filter((tag) => tag.tagGroupId !== tagGroupId);
            });
            queryClient.setQueriesData<ITagGroup[]>(Route.TagGroups, (groups) => {
                return (groups ?? []).filter((group) => group.tagGroupId !== tagGroupId);
            });
            toast.success(`Successfully deleted "${tagGroup}"`);
        } catch (serverError) {
            toast.error(getServerError(serverError));
        } finally {
            closeModal();
        }
    };

    return (
        <>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faTrash}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Delete
            </EllipsisMenuItem>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Delete Tag Group</h2>
                </ModalHeader>
                <ModalContent>
                    <p>
                        Are you sure you want to remove the "{tagGroup}" group? This will remove the group for all
                        users, including the tags within this group.
                    </p>
                </ModalContent>
                <ModalFooter>
                    <Button icon={faTrashAlt} onClick={deleteTagGroup} styleName="negative">
                        Yes, delete group
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default memo(DeleteTagGroup);
